import { useQuery } from '@apollo/client';
import axios from 'axios';
import { graphql, useStaticQuery } from 'gatsby';
import { parse } from 'query-string';
import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';

import FolxLoader from '@ui/atoms/FolxLoader';

import { ApiEndpoints } from '@utils/endpoints';

import { useAuth } from '@providers/AuthProvider';

import CURRENT_MEMBER_WITH_NAME_QUERY from '@graphql/queries/currentMemberWithName.gql';

import theme from '@theme/WsTheme';

import UpdateNameComponent from './UpdateName.component';
import ZendeskForm from './ZendeskForm.component';

export const PageWrapper = styled.div`
	flex-flow: column;
	background: ${theme.colors.lightBeige};
	position: relative;
	max-width: 120rem;
	margin-left: auto;
	margin-right: auto;
`;

const Headline = styled.div`
	font-size: ${theme.fontSizes.header.lg};
	padding: 2rem;
`;

const HeaderContainer = styled.div`
	width: 100%;
	text-align: center;
	padding: 3rem;
`;

export interface ICurrentMember {
	id: string;
	affirmingFirstName: string;
	affirmingLastName: string;
	legalFirstName: string;
	legalLastName: string;
}

const ZendeskSSO: FC = () => {
	const { getAuthToken } = useAuth();
	const qParams = parse(location.search ?? '');
	const { return_to } = qParams;
	const [jwt, setJwt] = useState('');
	const [showNameComponent, setShowNameComponent] = useState(false);
	const [currentMember, setCurrentMember] = useState<ICurrentMember>();
	const [autoSubmit, setAutoSubmit] = useState(false);

	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "zendesk-sso" }) {
				zendesk_loading_label
			}
		}
	`);

	// Retrieve name
	const { data: nameData, error: nameError } = useQuery<{
		currentMember: {
			id: string;
			affirmingFirstName: string;
			affirmingLastName: string;
			legalFirstName: string;
			legalLastName: string;
		};
	}>(CURRENT_MEMBER_WITH_NAME_QUERY);

	const fetchJWT = async () => {
		const authorizationToken = await getAuthToken();
		const response = await axios
			.get(`${ApiEndpoints.memberPortal}/zendesk-jwt`, {
				headers: {
					authorizationToken,
				},
			})
			.catch((error) => console.error(error));
		if (!response) return;

		setJwt(response.data.token);
	};

	useEffect(() => {
		if (!nameData) return;

		setCurrentMember(nameData.currentMember);

		// Determine if the user has a name configured
		if (!nameData.currentMember.affirmingFirstName && !nameData.currentMember.affirmingLastName) {
			setShowNameComponent(true);
			return;
		}

		fetchJWT();
	}, [nameData]);

	useEffect(() => {
		if (jwt && return_to) {
			setAutoSubmit(true);
		}
	}, [jwt, return_to]);

	const handleNameUpdated = () => {
		console.log('Name has been updated');
		setShowNameComponent(false);
		fetchJWT();
	};

	if (nameError) {
		<PageWrapper>
			<HeaderContainer>
				<Headline>
					Something went wrong. Contact{' '}
					<a href="mailto:folxsupport@folxhealth.com">folxsupport@folxhealth.com</a>.
				</Headline>
			</HeaderContainer>
		</PageWrapper>;
	}

	if (showNameComponent) {
		return (
			<UpdateNameComponent handleNameUpdated={handleNameUpdated} currentMember={currentMember} />
		);
	}

	return (
		<PageWrapper>
			<HeaderContainer>
				<FolxLoader message={pageContent?.zendesk_loading_label} />
				<ZendeskForm jwt={jwt} returnTo={return_to} autoSubmit={autoSubmit} />
			</HeaderContainer>
		</PageWrapper>
	);
};
export default ZendeskSSO;
